import { createContext, useContext, useEffect, useState, useMemo } from "react";
import { http } from "../lib/api";
import { useAuth } from "./AuthContext";

const BranchCtx = createContext(null);
const LS_KEY = "activeBranchId";

export function BranchProvider({ children }) {
  const { user } = useAuth();
  const [branches, setBranches] = useState([]);
  // "all" means aggregated view — SUPER ADMIN ONLY. Otherwise a specific branch_id.
  const [activeBranch, setActiveBranch] = useState(() => localStorage.getItem(LS_KEY) || "");

  // Only the super admin can roam freely across branches / view the aggregate.
  const isSuper = user?.role === "super_admin";

  const allowedIds = useMemo(() => {
    if (isSuper) return branches.map((b) => b.id);
    return user?.branch_ids?.length ? user.branch_ids : (user?.branch_id ? [user.branch_id] : []);
  }, [isSuper, branches, user]);

  useEffect(() => {
    if (!user) { setBranches([]); return; }
    http.get("/branches").then((r) => {
      setBranches(r.data);
      if (!activeBranch && user?.branch_id) setActiveBranch(user.branch_id);
      const allowed = isSuper
        ? [...r.data.map((b) => b.id), "all"]
        : (user?.branch_ids?.length ? user.branch_ids : (user?.branch_id ? [user.branch_id] : []));
      if (activeBranch && !allowed.includes(activeBranch)) {
        setActiveBranch(user?.branch_id || allowed[0] || "");
      }
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  useEffect(() => {
    if (activeBranch) localStorage.setItem(LS_KEY, activeBranch);
  }, [activeBranch]);

  // Effective branch — computed first so setBranch can log the previous value.
  const effective = useMemo(() => {
    if (isSuper) return activeBranch || (user?.branch_id || "");
    const allowed = user?.branch_ids || (user?.branch_id ? [user.branch_id] : []);
    if (activeBranch && allowed.includes(activeBranch)) return activeBranch;
    return user?.branch_id || allowed[0] || "";
  }, [activeBranch, isSuper, user]);

  // Super admin roams freely; admin & kasir may only switch among the branches
  // the super admin assigned to them, and only when they have more than one.
  const canSwitch = isSuper || allowedIds.length > 1;

  const setBranch = (b) => {
    if (!canSwitch) return;
    const previousEffective = effective;
    const commit = (target) => {
      setActiveBranch(target);
      if (previousEffective && target && previousEffective !== target && previousEffective !== "all" && target !== "all") {
        http.post("/branch-moves", { from_branch_id: previousEffective, to_branch_id: target }).catch(() => {});
      }
    };
    if (isSuper) { commit(b); return; }
    if (allowedIds.includes(b)) commit(b);
  };

  const visibleBranches = useMemo(() => {
    if (isSuper) return branches;
    return branches.filter((b) => allowedIds.includes(b.id));
  }, [branches, isSuper, allowedIds]);

  const currentBranch = useMemo(
    () => branches.find((b) => b.id === effective),
    [branches, effective]
  );

  return (
    <BranchCtx.Provider value={{
      branches, visibleBranches,
      activeBranch: effective, setBranch, canSwitch, currentBranch,
      isAllView: effective === "all",
      canViewAll: isSuper,
      reloadBranches: () => http.get("/branches").then((r) => setBranches(r.data)),
    }}>{children}</BranchCtx.Provider>
  );
}

export const useBranch = () => useContext(BranchCtx);
